import { useState, useEffect, useRef } from 'react'
import { Modal, Button, Group, Text, Stack, Checkbox, Divider } from '@mantine/core'
import { IconCheck, IconX } from '@tabler/icons-react'
import { nameToDataUrl, EXPORT_FONT_SIZE } from './nameToSignatureDataUrl'

interface SignatureModalProps {
  opened: boolean
  onClose: () => void
  onConfirm: (signatureDataUrl: string, signatureDate: string) => void
  savedSignatureDataUrl?: string | null
  onSaveSignature?: (signatureDataUrl: string) => Promise<void>
}

const SIGNATURE_FONT_FAMILY = '"Dancing Script", cursive'

function formatDateNow(): string {
  const now = new Date()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const yyyy = now.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

export function SignatureModal({
  opened,
  onClose,
  onConfirm,
  savedSignatureDataUrl,
  onSaveSignature,
}: SignatureModalProps) {
  const [name, setName] = useState('')
  const [saveForFuture, setSaveForFuture] = useState(false)
  const [saving, setSaving] = useState(false)
  const editableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (opened) {
      document.fonts.load(`${EXPORT_FONT_SIZE}px "Dancing Script"`)
    } else {
      setName('')
      setSaveForFuture(false)
      if (editableRef.current) editableRef.current.textContent = ''
    }
  }, [opened])

  const handleConfirm = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const dataUrl = await nameToDataUrl(trimmed)
    if (saveForFuture && onSaveSignature) {
      setSaving(true)
      try {
        await onSaveSignature(dataUrl)
      } finally {
        setSaving(false)
      }
    }
    onConfirm(dataUrl, formatDateNow())
  }

  const handleUseSaved = () => {
    if (savedSignatureDataUrl) {
      onConfirm(savedSignatureDataUrl, formatDateNow())
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Provider Signature" size="lg" centered>
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          By signing below, I acknowledge that the patient has been supplied information regarding
          and consented to undergo genetic testing, as set forth in Labcorp Genetics&apos; Informed
          Consent for Genetic Testing.
        </Text>

        {savedSignatureDataUrl && (
          <>
            <div>
              <Text size="sm" fw={500} mb={4}>
                Saved Signature
              </Text>
              <Group gap="sm" align="flex-end">
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2">
                  <img
                    src={savedSignatureDataUrl}
                    alt="Saved signature"
                    style={{ height: 40, width: 'auto' }}
                  />
                </div>
                <Button variant="light" size="sm" onClick={handleUseSaved}>
                  Use Saved Signature
                </Button>
              </Group>
            </div>
            <Divider label="or sign below" labelPosition="center" />
          </>
        )}

        <div>
          <Text size="xs" c="dimmed" mb={6}>
            Click below and type your full name
          </Text>
          <div
            className="rounded-md border border-gray-300 bg-white"
            style={{ padding: '12px 16px 8px', position: 'relative' }}
          >
            {!name && (
              <div
                style={{
                  position: 'absolute',
                  fontFamily: SIGNATURE_FONT_FAMILY,
                  fontSize: 48,
                  fontWeight: 700,
                  color: '#d1d5db',
                  pointerEvents: 'none',
                  lineHeight: 1.2,
                }}
              >
                Type to sign...
              </div>
            )}
            <div
              ref={editableRef}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setName(e.currentTarget.textContent ?? '')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault()
              }}
              style={{
                fontFamily: SIGNATURE_FONT_FAMILY,
                fontSize: 48,
                fontWeight: 700,
                color: '#000000',
                borderBottom: '1px solid rgba(180,180,180,0.8)',
                outline: 'none',
                minHeight: 60,
                lineHeight: 1.2,
                paddingBottom: 4,
              }}
            />
          </div>
        </div>

        {onSaveSignature && (
          <Checkbox
            label="Save this signature for future orders"
            checked={saveForFuture}
            onChange={(e) => setSaveForFuture(e.target.checked)}
            size="sm"
          />
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" leftSection={<IconX size={16} />} onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleConfirm}
            disabled={!name.trim()}
            loading={saving}
          >
            Confirm Signature
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
